import { Suspense, lazy, useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { shouldShowCopilot } from "@/lib/copilot-context";

// Heavy chat panel (ai-sdk + markdown) is code-split: it only downloads the
// first time a user actually opens the assistant.
const CopilotPanel = lazy(() => import("./copilot-panel"));

/**
 * Floating, page-aware MAKAI AI assistant.
 * Mounted globally; reads current pathname to provide context-aware suggestions
 * and forwards the surface to /api/copilot so the model stays on-task.
 */
export function CopilotFAB() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setAuthed(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(!!session);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  // Inline "Ask MAKAI" callouts dispatch `makai:ask` with a prompt string.
  useEffect(() => {
    function onAsk(e: Event) {
      const detail = (e as CustomEvent<{ prompt?: string }>).detail;
      if (!detail?.prompt) return;
      setPendingPrompt(detail.prompt);
      setOpen(true);
    }
    window.addEventListener("makai:ask", onAsk as EventListener);
    return () => window.removeEventListener("makai:ask", onAsk as EventListener);
  }, []);

  // Hide on auth pages, tutor surface, or the public landing page.
  if (!shouldShowCopilot(path)) return null;
  if (authed === false) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          // Warm the chat chunk on hover/touch so opening feels instant.
          onPointerEnter={() => { void import("./copilot-panel"); }}
          aria-label="Ask MAKAI AI"
          className="group fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-primary/40 transition-all hover:scale-[1.03] hover:shadow-xl md:bottom-6 md:right-6"
        >
          <span className="relative flex h-6 w-6 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-primary-foreground/30" />
            <Sparkles className="relative h-4 w-4" />
          </span>
          <span className="hidden sm:inline">Ask MAKAI AI</span>
        </button>
      )}
      {open && (
        <Suspense fallback={<CopilotSkeleton />}>
          <CopilotPanel
            path={path}
            initialPrompt={pendingPrompt}
            onConsumePrompt={() => setPendingPrompt(null)}
            onClose={() => { setPendingPrompt(null); setOpen(false); }}
          />
        </Suspense>
      )}
    </>
  );
}

function CopilotSkeleton() {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end sm:p-4" aria-busy="true">
      <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" />
      <div className="relative flex h-[88vh] w-full flex-col items-center justify-center gap-2 rounded-t-2xl border bg-background shadow-2xl sm:h-[640px] sm:max-h-[90vh] sm:w-[420px] sm:rounded-2xl">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <p className="text-xs text-muted-foreground">Starting MAKAI AI…</p>
      </div>
    </div>
  );
}
